"use client"

import type { CSSProperties } from "react"
import React from "react"

import { cn } from "@productlane/lib/src/browser"

/**
 * muload "Helix" loader: a 5x5 dot matrix with a double-arm brightness wave
 * sweeping around the grid, phased by each dot's angular position. Monochrome
 * by default (inherits the surrounding text color), self-contained so it
 * renders identically across the dashboard, web, and widget bundlers.
 * Collapses to a soft pulse under prefers-reduced-motion.
 */
const GRID = 5

/** Per-dot normalized angular position (0-1) driving the wave phase offset. */
export function computeHelixDots(grid = GRID) {
	const center = (grid - 1) / 2
	return Array.from({ length: grid * grid }, (_, i) => {
		const col = i % grid
		const row = Math.floor(i / grid)
		const normalized =
			(Math.atan2(row - center, col - center) + Math.PI * 2.5) % (Math.PI * 2)
		return { i, angle: normalized / (Math.PI * 2) }
	})
}

/** Resolve pixel geometry from the `size` prop, matching the legacy footprint. */
export function loaderDimensions(size: number) {
	const total = size * 3
	const dot = total / 8
	return { total, dot, gap: dot * 0.75 }
}

/**
 * muload "RAG Retrieve": the outer ring scans clockwise from the top, then the
 * retrieved chunks converge on the centre. Per-dot delays in ms, row by row.
 */
export const RETRIEVE_DELAYS_MS = [
	1400, 1481.9, 0, 118.1, 200, 1318.1, 2454.7, 1871.5, 2158.7, 281.9, 1200,
	2442.2, 2600, 1975.5, 400, 1081.9, 2413.2, 2179.6, 1865.6, 518.1, 1000, 918.1,
	800, 681.9, 600,
] as const

/** Whole-pixel dots and gaps: a fractional size lands dots on subpixels and draws them as ovals. */
export function crispLoaderDimensions(size: number) {
	const dot = Math.max(2, Math.round((size * 3) / 8))
	const gap = Math.max(1, Math.round(dot * 0.75))
	return { total: dot * GRID + gap * (GRID - 1), dot, gap }
}

const DOTS = computeHelixDots()

type LoaderProps = React.HTMLAttributes<HTMLSpanElement> & {
	/** Controls loader size in pixels via `size * 3`. Default: 5 */
	size?: number
	/** Animation speed multiplier. 1 = default. */
	speed?: number
	/** Dot color. Any CSS color. Defaults to the inherited text color. */
	color?: string
	/** "helix" sweeps a wave around the grid; "retrieve" scans the ring, then converges on the centre. */
	variant?: "helix" | "retrieve"
}

export const Loader = React.memo(
	React.forwardRef<HTMLSpanElement, LoaderProps>(
		(
			{
				className,
				size = 5,
				speed = 1,
				color,
				style,
				variant = "helix",
				...props
			},
			ref,
		) => {
			const retrieve = variant === "retrieve"
			const { dot, gap } = retrieve
				? crispLoaderDimensions(size)
				: loaderDimensions(size)
			const duration = (retrieve ? 3 : 2.6) / speed

			return (
				<>
					<style>{`
						@keyframes pl-helix {
							0%, 100% { opacity: 0.1; }
							30% { opacity: 0.95; }
							50% { opacity: 0.3; }
							80% { opacity: 0.95; }
						}
						@keyframes pl-retrieve {
							0%, 100% { opacity: 0.12; transform: translateZ(0) scale(1); }
							6% { opacity: 1; transform: translateZ(0) scale(1.2); }
							16% { opacity: 0.12; transform: translateZ(0) scale(1); }
						}
						@keyframes pl-helix-pulse {
							0%, 100% { opacity: 0.4; }
							50% { opacity: 1; }
						}
						@media (prefers-reduced-motion: reduce) {
							.pl-loader { animation: pl-helix-pulse 2s ease-in-out infinite; }
							.pl-loader .pl-helix-dot,
							.pl-loader .pl-retrieve-dot {
								animation: none !important;
								opacity: 0.6 !important;
								transform: none !important;
							}
						}
					`}</style>
					<span
						ref={ref}
						role="status"
						aria-label="Loading"
						className={cn("pl-loader inline-grid", className)}
						style={
							{
								display: "inline-grid",
								gridTemplateColumns: `repeat(${GRID}, ${dot}px)`,
								gap,
								width: "max-content",
								"--pl-dot": color ?? "var(--color-dot, currentColor)",
								...style,
							} as CSSProperties
						}
						{...props}
					>
						{retrieve
							? RETRIEVE_DELAYS_MS.map((delay, i) => (
									<span
										key={i}
										className="pl-retrieve-dot"
										style={{
											width: dot,
											height: dot,
											borderRadius: "50%",
											background: "var(--pl-dot)",
											opacity: 0.12,
											willChange: "opacity, transform",
											animation: `pl-retrieve ${duration}s cubic-bezier(0.45,0.05,0.55,0.95) ${
												delay / 1000 / speed
											}s infinite`,
										}}
									/>
								))
							: DOTS.map((d) => (
									<span
										key={d.i}
										className="pl-helix-dot"
										style={{
											width: dot,
											height: dot,
											borderRadius: "50%",
											background: "var(--pl-dot)",
											opacity: 0.1,
											willChange: "opacity",
											animation: `pl-helix ${duration}s cubic-bezier(0.45,0.05,0.55,0.95) ${
												d.angle * -duration
											}s infinite`,
										}}
									/>
								))}
					</span>
				</>
			)
		},
	),
)
